import { useState } from 'react';
import { css } from '@emotion/react';
import { AppointmentAggregatesResponse } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { useHasAdminAccess } from '@frontend/analytics-shared';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';

interface Props {
  aggregatesData: AppointmentAggregatesResponse | undefined;
  aggregatesDataLoading: boolean;
}

export const TopBanner = (props: Props) => {
  const { aggregatesData, aggregatesDataLoading } = props;

  const data = aggregatesData?.data ? aggregatesData.data[0] : undefined;
  const totalAppointments = data?.totalAppointments ?? 0;
  const completedAppointments = data?.completedAppointments ?? 0;
  const cancelledAppointments = data?.cancelledAppointments ?? 0;
  const confirmedAppointments = data?.confirmedAppointments ?? 0;
  const noShowCount = data?.noShowCount ?? 0;

  const [show, setShow] = useState<boolean>(true);
  const { t } = useTranslation('analytics');

  let condition = '';

  if (totalAppointments > 0) {
    const completionRate = (completedAppointments / totalAppointments) * 100;
    const cancellationRate = (cancelledAppointments / totalAppointments) * 100;
    const confirmationRate = (confirmedAppointments / totalAppointments) * 100;
    const noShowRate = (noShowCount / totalAppointments) * 100;

    if (completionRate < 20 || completionRate > 95) {
      condition = 'Completion rate';
    } else if (cancellationRate < 2 || cancellationRate > 30) {
      condition = 'Cancellation rate';
    } else if (confirmationRate < 5 || confirmationRate > 80) {
      condition = 'Confirmation rate';
    } else if (noShowRate < 5 || noShowRate > 30) {
      condition = 'No show rate';
    }
  }

  if (aggregatesDataLoading || !show || !condition) {
    return null;
  }
  return (
    <div css={styles.container}>
      <Icon name='alert-filled' color='error' size={24} />
      <Trans t={t} values={{ condition }}>
        <Text weight='bold'>{{ condition }}</Text> is out of normal range, if you think it's not accurate then please
        contact your admin to review status mapping under settings.
      </Trans>
      <ReviewStatusMappingLink />
      <Button
        iconName='x'
        onClick={() => setShow(false)}
        size='small'
        variant='secondary'
        css={styles.hideBannerButton}
      />
    </div>
  );
};

const ReviewStatusMappingLink = () => {
  const hasAdminAccess = useHasAdminAccess();
  const { t } = useTranslation('analytics');

  const { navigate: settingsNavigate } = useSettingsNavigate();

  const handleViewReminderSettingsClick = () => {
    settingsNavigate({ to: '/organization/data-sync' });
  };

  // This link is only visible to users with admin access
  if (!hasAdminAccess) {
    return null;
  }

  return (
    <Button
      variant='tertiary'
      onClick={handleViewReminderSettingsClick}
      trackingId='past-appointments-review-mapping-button'
    >
      {t('Review Mapping')}
    </Button>
  );
};

const styles = {
  container: css`
    display: flex;
    gap: ${theme.spacing(1)};
    align-items: center;
    padding: ${theme.spacing(2)};
    border: 1px solid ${theme.colorsV2.yellow20()};
    border-radius: ${theme.borderRadius.medium};
    background-color: ${theme.colorsV2.yellow5()};
  `,
  hideBannerButton: css`
    margin-left: auto;
  `,
};
